import React, { useState, useRef, useEffect } from 'react';
import { Upload, X, Image as ImageIcon, Check } from 'lucide-react';

const ImageUpload = ({ value, onChange, label = "Featured Image" }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [preview, setPreview] = useState(value || '');
  const [uploadStatus, setUploadStatus] = useState('');
  const fileInputRef = useRef(null);

  // Update preview when value prop changes (for edit mode)
  useEffect(() => {
    if (value && value !== preview) {
      setPreview(value);
    }
  }, [value]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleFile = (file) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    setUploadStatus('uploading');

    // Create preview using FileReader
    const reader = new FileReader();
    reader.onload = (e) => {
      const imageUrl = e.target.result;
      setPreview(imageUrl);
      
      // In production, you would upload to server/cloud here
      // For now, we're using base64 data URL
      onChange(imageUrl);
      
      setUploadStatus('success');
      setTimeout(() => setUploadStatus(''), 2000);
    };
    reader.readAsDataURL(file);
  };

  const handleUrlInput = (url) => {
    setPreview(url);
    onChange(url);
  };

  const clearImage = () => {
    setPreview('');
    onChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-4">
      <label className="block text-sm font-bold text-gray-700 flex items-center gap-2">
        <ImageIcon className="w-4 h-4" />
        {label}
      </label>

      {/* Drag & Drop Zone */}
      {!preview && (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all ${
            isDragging 
              ? 'border-primary bg-blue-50' 
              : 'border-gray-300 hover:border-primary hover:bg-gray-50'
          }`}
        >
          <Upload className={`w-12 h-12 mx-auto mb-4 ${isDragging ? 'text-primary' : 'text-gray-400'}`} />
          <p className="text-gray-700 font-semibold mb-2">
            {isDragging ? 'Drop image here' : 'Drag & drop image here'}
          </p>
          <p className="text-sm text-gray-500 mb-4">or</p>
          <button
            type="button"
            className="bg-primary hover:bg-primary-700 text-white px-6 py-2 rounded-lg transition-colors font-semibold"
          >
            Browse Files
          </button>
          <p className="text-xs text-gray-500 mt-4">
            Supported: JPG, PNG, GIF (Max 5MB)
          </p>
          
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>
      )}

      {/* Upload Status */}
      {uploadStatus === 'uploading' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            <span className="text-blue-800 font-semibold">Uploading image...</span>
          </div>
        </div>
      )}

      {uploadStatus === 'success' && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <Check className="w-6 h-6 text-green-600" />
            <span className="text-green-800 font-semibold">Image uploaded successfully!</span>
          </div>
        </div>
      )}

      {/* Image Preview */}
      {preview && (
        <div className="relative">
          <img 
            src={preview} 
            alt="Preview" 
            className="w-full h-64 object-cover rounded-lg border-2 border-gray-200"
            onError={(e) => {
              // Use a data URL as fallback instead of external placeholder
              e.target.onerror = null; // Prevent infinite loop
              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAwIiBoZWlnaHQ9IjQwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iODAwIiBoZWlnaHQ9IjQwMCIgZmlsbD0iIzFlM2E4YSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMjQiIGZpbGw9IiNmYmJmMjQiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBFcnJvcjwvdGV4dD48L3N2Zz4=';
            }}
          />
          <button
            type="button"
            onClick={clearImage}
            className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white p-2 rounded-full transition-colors shadow-lg"
            title="Remove Image"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Manual URL Input (Fallback) */}
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-2">
          Or enter image URL manually:
        </label>
        <input
          type="text"
          value={preview}
          onChange={(e) => handleUrlInput(e.target.value)}
          placeholder="/images/blog/post.jpg or https://..."
          className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none transition-colors text-sm"
        />
        <p className="text-xs text-gray-500 mt-1">
          Recommended size: 800x400px
        </p>
      </div>
    </div>
  );
};

export default ImageUpload;
